import { fetchWithNonce } from './fetchWithNonce';
import { parseApiError } from './parseApiError';

export type SignatureStep =
  | 'not_configured'
  | 'not_connected'
  | 'ready'
  | 'in_progress'
  | 'import_pending'
  | 'complete'
  // The collecte was closed on jesignexpert.com before every signatory signed: the mission keeps
  // its status, the partially signed document and the attestation can be downloaded, and a new
  // collecte may be launched.
  | 'partial'
  | 'done';

export type MissionSignatureConnectionStatus = {
  configured: boolean;
  connected: boolean;
  office_name: string | null;
  user_connected: boolean;
};

export type MissionSignatureCollection = {
  id: number;
  jse_transaction_id: string;
  state: string;
  signed_attachment_id: number;
  attestation_attachment_id: number;
  signing_url: string;
  updated_at: string;
};

export type MissionSignatureStatus = {
  company_signatory?: {
    designated: boolean;
    complete: boolean;
    display_name: string;
  };
  configured: boolean;
  connection: MissionSignatureConnectionStatus;
  can_sign: boolean;
  can_start: boolean;
  // The actor may call the mutation routes (start, sync, relaunch, reset): canMutateMission, while
  // this status only needs canReadMission.
  can_mutate: boolean;
  can_resume_signing: boolean;
  collection: MissionSignatureCollection | null;
  signing_url: string | null;
  email_pivot_warning: string | null;
  signed_download_available: boolean;
  signed_download_url: string | null;
  attestation_download_available: boolean;
  attestation_download_url: string | null;
  promotion_pending?: boolean;
  mission_promoted?: boolean;
  step: SignatureStep;
};

export type MissionSignatureStartResult = {
  collection_id: number;
  jse_transaction_id: string;
  state: string;
  signing_url: string;
};

type MissionSignatureApiConfig = {
  hubBaseUrl: string;
  nonce: string;
};

const parseSignaturePayload = (payload: unknown): MissionSignatureStatus | null => {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const data = (payload as { data?: MissionSignatureStatus }).data;
  return data ?? null;
};

export const fetchMissionSignatureStatus = async (
  config: MissionSignatureApiConfig,
  missionId: number,
): Promise<MissionSignatureStatus> => {
  const response = await fetchWithNonce(`${config.hubBaseUrl}missions/${missionId}/signature`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const status = parseSignaturePayload(await response.json());
  if (status === null) {
    throw new Error('Réponse serveur invalide.');
  }

  return status;
};

export const startMissionSignature = async (
  config: MissionSignatureApiConfig,
  missionId: number,
  includeCompanySignatory = false,
): Promise<MissionSignatureStartResult> => {
  const response = await fetchWithNonce(`${config.hubBaseUrl}missions/${missionId}/signature`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({ include_company_signatory: includeCompanySignatory }),
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as { data?: MissionSignatureStartResult };
  if (payload.data === undefined || typeof payload.data.signing_url !== 'string') {
    throw new Error('Réponse serveur invalide.');
  }

  return payload.data;
};

export const resetMissionSignature = async (
  config: MissionSignatureApiConfig,
  missionId: number,
): Promise<MissionSignatureStatus> => {
  const response = await fetchWithNonce(`${config.hubBaseUrl}missions/${missionId}/signature/reset`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const status = parseSignaturePayload(await response.json());
  if (status === null) {
    throw new Error('Réponse serveur invalide.');
  }

  return status;
};

export const syncMissionSignature = async (
  config: MissionSignatureApiConfig,
  missionId: number,
): Promise<MissionSignatureStatus> => {
  const response = await fetchWithNonce(`${config.hubBaseUrl}missions/${missionId}/signature/sync`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const status = parseSignaturePayload(await response.json());
  if (status === null) {
    throw new Error('Réponse serveur invalide.');
  }

  return status;
};

export type MissionSignatureRelaunchResult = {
  relaunched: true;
  // Unix seconds of the collecte's new expiry at JeSignExpert, when it reports one.
  expiration_date: number | null;
};

const isRelaunchResult = (data: unknown): data is MissionSignatureRelaunchResult => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const { relaunched, expiration_date: expirationDate } = data as Record<string, unknown>;
  return relaunched === true && (expirationDate === null || typeof expirationDate === 'number');
};

// Asks JeSignExpert to re-send the invitation to the signatory of the collecte in flight. Errors
// carry the server's French `message` (409 not in progress / expired / JSE not connected, 429 too
// soon, 502 JSE refused), surfaced as-is by parseApiError.
export const relaunchMissionSignature = async (
  config: MissionSignatureApiConfig,
  missionId: number,
): Promise<MissionSignatureRelaunchResult> => {
  const response = await fetchWithNonce(`${config.hubBaseUrl}missions/${missionId}/signature/relaunch`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload: unknown = await response.json();
  const data = typeof payload === 'object' && payload !== null ? (payload as { data?: unknown }).data : undefined;
  if (!isRelaunchResult(data)) {
    throw new Error('Réponse serveur invalide.');
  }

  return data;
};

export type MissionSignatureFile = {
  blob: Blob;
  filename: string | null;
};

// `attachment; filename="<rawurlencoded name>"` as sent by MissionSignatureRestController.
const filenameFromDisposition = (header: string | null): string | null => {
  const match = header === null ? null : /filename="([^"]+)"/.exec(header);
  if (match === null) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

// The signed-report / attestation routes are REST routes: a plain link would reach them without
// the X-WP-Nonce header, so WordPress would treat the request as anonymous. Fetch with the nonce
// and hand back the file.
export const downloadMissionSignatureFile = async (
  config: MissionSignatureApiConfig,
  url: string,
): Promise<MissionSignatureFile> => {
  const response = await fetchWithNonce(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('Content-Disposition')),
  };
};
